import { Controller, useFormContext } from 'react-hook-form';
import { Input, FormFeedback, Label, FormGroup } from '@polichat/flamboyant';
import styles from '../../styles.module.css';
import { CurrencyField } from '../field/CurrencyField';
import {
  ORDER_PAYMENT_OPTIONS,
  ORDER_PAYMENT_VALUES,
  ORDER_SALE_STATUS_OPTIONS,
  ORDER_SALES_STATUS_VALUES,
  ORDER_STATUS_OPTIONS,
  ORDER_STATUS_VALUES,
} from '../../useFinishChatForm/types';
import { useGlotaT } from 'src/hooks/useGlotaTranslation';
import { useMemo } from 'react';

export function ProductForm() {
  const { control } = useFormContext();

  // Traduções
  const orderDetailsText = useGlotaT('spa.chat.finishChat.orderDetails');
  const productText = useGlotaT('spa.chat.finishChat.product');
  const productNamePlaceholderText = useGlotaT(
    'spa.chat.finishChat.productNamePlaceholder'
  );
  const valueText = useGlotaT('spa.chat.finishChat.value');
  const valuePlaceholderText = useGlotaT(
    'spa.chat.finishChat.valuePlaceholder'
  );
  const paymentMethodText = useGlotaT('spa.chat.finishChat.paymentMethod');
  const statusText = useGlotaT('spa.chat.finishChat.status');

  // Traduções para opções de pagamento
  const creditCardText = useGlotaT('spa.chat.finishChat.creditCard');
  const debitText = useGlotaT('spa.chat.finishChat.debit');
  const pixText = useGlotaT('spa.chat.finishChat.pix');
  const bankSlipText = useGlotaT('spa.chat.finishChat.bankSlip');
  const cashText = useGlotaT('spa.chat.finishChat.cash');

  // Traduções para status
  const paidText = useGlotaT('spa.chat.finishChat.paid');
  const openText = useGlotaT('spa.chat.finishChat.open');
  const canceledText = useGlotaT('spa.chat.finishChat.canceled');

  // Traduções para status de venda
  const singleSaleText = useGlotaT('spa.chat.finishChat.singleSale');
  const recurringSaleText = useGlotaT('spa.chat.finishChat.recurringSale');

  // Opções traduzidas
  const translatedPaymentOptions = useMemo(
    () =>
      ORDER_PAYMENT_OPTIONS.map((opt) => {
        let label = opt.label;
        if (opt.value === ORDER_PAYMENT_VALUES.credit) label = creditCardText;
        else if (opt.value === ORDER_PAYMENT_VALUES.debit) label = debitText;
        else if (opt.value === ORDER_PAYMENT_VALUES.pix) label = pixText;
        else if (opt.value === ORDER_PAYMENT_VALUES.bank_slip)
          label = bankSlipText;
        else if (opt.value === ORDER_PAYMENT_VALUES.cash) label = cashText;
        return { ...opt, label };
      }),
    [creditCardText, debitText, pixText, bankSlipText, cashText]
  );

  const translatedStatusOptions = useMemo(
    () =>
      ORDER_STATUS_OPTIONS.map((opt) => {
        let label = opt.label;
        if (opt.value === ORDER_STATUS_VALUES.paid) label = paidText;
        else if (opt.value === ORDER_STATUS_VALUES.open) label = openText;
        else if (opt.value === ORDER_STATUS_VALUES.canceled)
          label = canceledText;
        return { ...opt, label };
      }),
    [paidText, openText, canceledText]
  );

  const translatedSaleStatusOptions = useMemo(
    () =>
      ORDER_SALE_STATUS_OPTIONS.map((opt) => {
        let label = opt.label;
        if (opt.value === ORDER_SALES_STATUS_VALUES['one-time-sale'])
          label = singleSaleText;
        else if (opt.value === ORDER_SALES_STATUS_VALUES['recurring-sale'])
          label = recurringSaleText;
        return { ...opt, label };
      }),
    [singleSaleText, recurringSaleText]
  );

  return (
    <div
      className={styles.fieldContainer}
      data-testid="finish-chat-product-container"
    >
      <h3 className={styles.fieldTitle}>{orderDetailsText}</h3>
      <div className={styles.gridContainer}>
        <div className={styles.gridItem}>
          <Controller
            name="commercial.order.name"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Label
                  htmlFor="productName"
                  className={styles.fieldLabel}
                  data-testid="finish-chat-product-name-label"
                >
                  {productText}
                  <span className={styles.fieldLabelRequired}>*</span>:
                </Label>
                <Input
                  id="productName"
                  type="text"
                  {...field}
                  placeholder={productNamePlaceholderText}
                  invalid={!!fieldState.error}
                  data-testid="finish-chat-product-name-input"
                />
                {fieldState.error && (
                  <FormFeedback data-testid="finish-chat-product-name-error">
                    {fieldState?.error?.message}
                  </FormFeedback>
                )}
              </>
            )}
          />
        </div>
        <div className={styles.gridItem}>
          <Controller
            name="commercial.order.value"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Label
                  htmlFor="productValue"
                  className={styles.fieldLabel}
                  data-testid="finish-chat-product-value-label"
                >
                  {valueText}
                  <span className={styles.fieldLabelRequired}>*</span>:
                </Label>
                <CurrencyField
                  id="productValue"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={valuePlaceholderText}
                  error={fieldState.error?.message}
                  data-testid="finish-chat-product-value-input"
                />
                {fieldState.error && (
                  <FormFeedback data-testid="finish-chat-product-value-error">
                    {fieldState?.error?.message}
                  </FormFeedback>
                )}
              </>
            )}
          />
        </div>
        <div className={styles.gridItem}>
          <Controller
            name="commercial.order.payment"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Label
                  htmlFor="productPayment"
                  className={styles.fieldLabel}
                  data-testid="finish-chat-product-payment-label"
                >
                  {paymentMethodText}
                  <span className={styles.fieldLabelRequired}>*</span>:
                </Label>
                <Input
                  id="productPayment"
                  type="select"
                  {...field}
                  invalid={!!fieldState.error}
                  data-testid="finish-chat-product-payment-select"
                >
                  {translatedPaymentOptions.map((payment) => (
                    <option
                      key={payment.value}
                      value={payment.value}
                      data-testid={`finish-chat-product-payment-option-${payment.value}`}
                    >
                      {payment.label}
                    </option>
                  ))}
                </Input>
                {fieldState.error && (
                  <FormFeedback data-testid="finish-chat-product-payment-error">
                    {fieldState?.error?.message}
                  </FormFeedback>
                )}
              </>
            )}
          />
        </div>
        <div className={styles.gridItem}>
          <Controller
            name="commercial.order.status"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Label
                  htmlFor="productStatus"
                  className={styles.fieldLabel}
                  data-testid="finish-chat-product-status-label"
                >
                  {statusText}
                  <span className={styles.fieldLabelRequired}>*</span>:
                </Label>
                <Input
                  id="productStatus"
                  type="select"
                  {...field}
                  invalid={!!fieldState.error}
                  data-testid="finish-chat-product-status-select"
                >
                  {translatedStatusOptions.map((status) => (
                    <option
                      key={status.value}
                      value={status.value}
                      data-testid={`finish-chat-product-status-option-${status.value}`}
                    >
                      {status.label}
                    </option>
                  ))}
                </Input>
                {fieldState.error && (
                  <FormFeedback data-testid="finish-chat-product-status-error">
                    {fieldState?.error?.message}
                  </FormFeedback>
                )}
              </>
            )}
          />
        </div>
        <div className={styles.gridItem}>
          <Controller
            name="commercial.order.sales_status"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <FormGroup tag="fieldset">
                  {translatedSaleStatusOptions?.map((status) => (
                    <FormGroup check key={status.value}>
                      <Input
                        type="radio"
                        id={status.value}
                        name="commercial.order.sales_status"
                        value={status.value}
                        checked={field.value === status.value}
                        onChange={field.onChange}
                        data-testid={`finish-chat-product-sales-status-option-${status.value}`}
                      />
                      <Label
                        check
                        htmlFor={status.value}
                        data-testid={`finish-chat-product-sales-status-label-${status.value}`}
                      >
                        {status.label}
                      </Label>
                    </FormGroup>
                  ))}
                </FormGroup>
                {fieldState?.error?.message && (
                  <span
                    className={styles.fieldError}
                    data-testid="finish-chat-product-sales-status-error"
                  >
                    {fieldState?.error?.message}
                  </span>
                )}
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
}
